import { useState, useRef, useEffect, lazy, Suspense, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { detectAndConvert } from "@/lib/keyboardMapper";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ShoppingCart, Heart, User, Menu, X, ChevronDown,
  Store, LogOut, ShieldCheck,
  Sun, Moon, Camera, Package, Ticket, Coins, Star, Headphones,
  Settings, ChevronLeft, ArrowLeftRight, Sparkles, BookOpen,
} from "lucide-react";
import { useDynamicCategories } from "@/hooks/useDynamicCategories";
import { useSubcategoryImages } from "@/hooks/useSubcategoryImages";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/hooks/useCart";
import { useTheme } from "@/hooks/useTheme";
import { usePoints } from "@/hooks/usePoints";
import { useWishlist } from "@/hooks/useWishlist";
import PointsBadge from "@/components/PointsBadge";
const DailyReward = lazy(() => import("@/components/DailyReward"));
import SubcategoryCircle from "@/components/SubcategoryCircle";
import AnimatedSmileyPattern from "@/components/AnimatedSmileyPattern";
import smileylandLogo from "@/assets/smileygift-logo.png";
const VisualSearchModal = lazy(() => import("@/components/VisualSearchModal"));
const NotificationsDropdown = lazy(() => import("@/components/NotificationsDropdown"));
const UserNotificationsDropdown = lazy(() => import("@/components/UserNotificationsDropdown"));
const LanguageSwitcher = lazy(() => import("@/components/LanguageSwitcher"));
import { supabase } from "@/integrations/supabase/client";
import { productUrl } from "@/lib/productUrl";

// Categories loaded dynamically from useDynamicCategories hook

const NavIconButton = ({ children, className = "", ...props }: any) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    className={`p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl hover:bg-primary/10 transition-all duration-200 relative group hover:shadow-[0_0_12px_hsl(var(--primary)/0.2)] text-primary [&_svg]:drop-shadow-[0_0_6px_hsl(45_100%_50%/0.7)] hover:[&_svg]:drop-shadow-[0_0_12px_hsl(45_100%_50%/0.9)] ${className}`}
    {...props}
  >
    {children}
  </motion.button>
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [visualSearchOpen, setVisualSearchOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [hoveredCat, setHoveredCat] = useState<number | null>(null);
  const [mobileExpandedCats, setMobileExpandedCats] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchPreview, setShowSearchPreview] = useState(false);
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const { user, isVendor, isAdmin, signOut } = useAuth();
  const { itemCount, setIsOpen: openCart } = useCart();
  const { theme, toggleTheme, isDark } = useTheme();
  const { balance: pointsBalance } = usePoints();
  const { count: wishlistCount } = useWishlist();
  const { data: dynamicCategories } = useDynamicCategories();
  const navigate = useNavigate();
  const location = useLocation();

  // Live search preview
  const { data: searchPreviewResults } = useQuery({
    queryKey: ["search-preview", searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim() || searchQuery.trim().length < 2) return [];
      const raw = searchQuery.trim();
      const converted = detectAndConvert(raw);
      const q = converted || raw;
      const { data } = await supabase
        .from("products")
        .select("id, name, price, original_price, image_url, category")
        .eq("is_published", true)
        .gt("price", 0)
        .or(`name.ilike.%${q}%,short_description.ilike.%${q}%,category.ilike.%${q}%`)
        .order("sold_count", { ascending: false })
        .limit(6);
      return data || [];
    },
    enabled: searchQuery.trim().length >= 2,
    staleTime: 30000,
  });

  const handleSearchSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/חיפוש?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchPreview(false);
      setSearchFocused(false);
      setMobileSearchOpen(false);
      setSearchQuery("");
    }
  }, [searchQuery, navigate]);

  // Close preview on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchPreview(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Collect all subcategory names for image fetching
  const allSubNames = useMemo(() => {
    if (!dynamicCategories) return [];
    return dynamicCategories.flatMap(g => g.subcategories.map(s => s.name));
  }, [dynamicCategories]);

  const { data: subImages } = useSubcategoryImages(allSubNames);

  // Build categories array from dynamic data with subcategory image mapping
  const categories = useMemo(() => {
    if (!dynamicCategories) return [];
    return dynamicCategories.map(g => {
      // Use first subcategory image as parent circle image fallback
      let parentCircle = g.circleImage;
      if ((!parentCircle || parentCircle.includes("placeholder")) && subImages) {
        for (const s of g.subcategories) {
          const img = subImages.get(s.name);
          if (img) { parentCircle = img; break; }
        }
      }
      return {
        name: g.name,
        slug: g.slug,
        icon: g.icon,
        color: g.color,
        circleImage: parentCircle,
        subcategories: g.subcategories.map(s => ({
          name: s.name,
          emoji: s.emoji,
          staticImage: s.image,
          dbImage: subImages?.get(s.name),
        })),
      };
    });
  }, [dynamicCategories, subImages]);

  // Detect active category from /קטגוריה/:slug URL
  const categorySlugMatch = decodeURIComponent(location.pathname).match(/^\/קטגוריה\/([^/]+)/);
  const activeCatSlug = categorySlugMatch ? categorySlugMatch[1] : null;
  const activeCatIndex = activeCatSlug ? categories.findIndex(c => c.slug === activeCatSlug) : -1;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (profileMenuRef.current && !profileMenuRef.current.contains(target)) {
        // Also check if click was on a trigger button
        const triggers = document.querySelectorAll('.profile-menu-trigger');
        for (const trigger of triggers) {
          if (trigger.contains(target)) return;
        }
        setProfileMenuOpen(false);
      }
    };
    if (profileMenuOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [profileMenuOpen]);

  return (
    <header className="sticky top-0 z-50">
      {/* ── Top promo bar — NEON GOLD ── */}
      <div className="relative overflow-hidden bg-primary shadow-[0_2px_20px_hsl(var(--primary)/0.6),0_4px_40px_hsl(var(--primary)/0.3)]">
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-[hsl(42,100%,45%)] to-primary opacity-90" />
        {/* Neon shimmer sweep */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 -left-[50%] w-[200%] h-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_3s_ease-in-out_infinite]" style={{ animationName: 'shimmer' }} />
        </div>
        <div className="container flex items-center justify-between py-1.5 md:py-2 relative">
          <div className="hidden md:flex items-center gap-4 text-[11px] text-primary-foreground/80">
            <Link to="/אודות" className="hover:text-primary-foreground transition-colors">אודות</Link>
            <Link to="/צור-קשר" className="hover:text-primary-foreground transition-colors">צור קשר</Link>
            <Link to="/מרכז-עזרה" className="hover:text-primary-foreground transition-colors">עזרה</Link>
          </div>
          <p className="text-[11px] sm:text-xs font-bold text-primary-foreground tracking-wide mx-auto md:mx-0">
            <span className="inline-flex items-center gap-2">
              <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 2, repeat: Infinity }}>🔥</motion.span>
              <span>משלוח חינם מעל ₪500</span>
              <span className="text-primary-foreground/50 mx-1">•</span>
              <span>🎁 50 נקודות מתנה לכל משתמש חדש!</span>
            </span>
          </p>
          <div className="hidden md:flex items-center gap-3">
          </div>
        </div>
      </div>

      {/* ── Neon border line between promo and nav ── */}
      <div className="relative h-[2px]">
        <div className="absolute inset-0 bg-primary/80" />
        <div className="absolute inset-0 bg-primary blur-[6px]" />
        <div className="absolute inset-0 bg-primary/40 blur-[15px]" />
      </div>

      {/* ── Main nav — NEON ENHANCED + SMILEY PATTERN ── */}
      <nav className="relative border-b border-primary/30 bg-card/70 backdrop-blur-xl shadow-[0_4px_30px_hsl(var(--primary)/0.12),0_8px_60px_hsl(var(--primary)/0.06)]">
        <div className="container relative flex items-center gap-2 md:gap-6 py-2 md:py-3">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 relative group flex items-center gap-2.5">
            <div className="relative">
              <div className="absolute inset-0 -m-3 rounded-full blur-[25px] bg-primary/30 group-hover:bg-primary/45 transition-all pointer-events-none" />
              <div className="absolute inset-0 -m-5 rounded-full blur-[50px] bg-primary/10 group-hover:bg-primary/20 transition-all pointer-events-none" />
              <motion.img
                src={smileylandLogo}
                alt="SmileyGift"
                width={160}
                height={60}
                className="relative h-10 md:h-16 w-auto object-contain drop-shadow-[0_0_20px_hsl(var(--primary)/0.7)]"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
              />
            </div>
            <div className="hidden sm:block">
              <p className="text-xl md:text-2xl font-black leading-tight">
                <span className="text-primary neon-text">SmileyGift</span>
                <span className="text-base md:text-lg font-bold" style={{ color: "hsl(45 20% 80%)" }}> — מתנות שעושות שמח</span>
              </p>
              <p className="text-[10px] md:text-xs" style={{ color: "hsl(45 15% 55%)" }}>כי לתת זה הדבר הכי טוב בעולם 😊</p>
            </div>
          </Link>

          {/* Spacer — desktop */}
          <div className="hidden md:block flex-1" />
          {/* Actions — mobile top bar */}
          <div className="flex md:hidden items-center gap-0.5">
            <NavIconButton onClick={() => setMobileSearchOpen(true)} title="חיפוש">
              <Search size={18} />
            </NavIconButton>

            <NavIconButton onClick={() => setVisualSearchOpen(true)} title="חיפוש לפי תמונה">
              <Camera size={18} />
            </NavIconButton>

            <NavIconButton onClick={toggleTheme} title={isDark ? "מצב יום" : "מצב לילה"}>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={theme}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.25 }}
                >
                  {isDark ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} />}
                </motion.div>
              </AnimatePresence>
            </NavIconButton>

            {/* Language switcher moved to side tab */}

            {isAdmin && location.pathname.startsWith("/ניהול") ? (
              <NotificationsDropdown variant="navbar" />
            ) : user ? (
              <UserNotificationsDropdown />
            ) : null}

            <NavIconButton className="relative" onClick={() => navigate("/מועדפים")} aria-label="מועדפים">
              <Heart size={18} />
              {wishlistCount > 0 && (
                <motion.span
                  key={wishlistCount}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                  className="absolute -top-0.5 -left-0.5 gradient-hot text-primary-foreground text-[9px] font-bold rounded-full flex items-center justify-center min-w-[16px] h-[16px] shadow-sm"
                >
                  {wishlistCount}
                </motion.span>
              )}
            </NavIconButton>

            <NavIconButton onClick={() => navigate("/בלוג")} aria-label="בלוג">
              <BookOpen size={18} className="text-primary" />
            </NavIconButton>

            {user ? (
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setProfileMenuOpen((v) => !v)}
                  className="flex items-center gap-1.5 py-1 px-2 rounded-xl hover:bg-muted/60 transition-all duration-200 profile-menu-trigger"
                >
                  <div className="w-7 h-7 rounded-full gradient-primary text-primary-foreground flex items-center justify-center text-[10px] font-black">
                    {(user.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] text-muted-foreground leading-none">שלום 👋</p>
                    <p className="text-[10px] font-bold text-foreground leading-tight mt-0.5 max-w-[60px] truncate">
                      {user.email?.split("@")[0] || "משתמש"}
                    </p>
                  </div>
                </motion.button>
              </div>
            ) : (
              <Link to="/כניסה" aria-label="כניסה לחשבון">
                <NavIconButton aria-label="כניסה לחשבון"><User size={18} /></NavIconButton>
              </Link>
            )}
          </div>

          {/* Actions — desktop */}
          <div className="hidden md:flex items-center gap-1">
            {/* Notifications */}
            {isAdmin && location.pathname.startsWith("/ניהול") ? (
              <NotificationsDropdown variant="navbar" />
            ) : user ? (
              <UserNotificationsDropdown />
            ) : null}

            {/* Wishlist */}
            <NavIconButton className="relative" onClick={() => navigate("/מועדפים")} aria-label="מועדפים">
              <Heart size={19} />
              {wishlistCount > 0 && (
                <motion.span
                  key={wishlistCount}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                  className="absolute -top-0.5 -left-0.5 gradient-hot text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center min-w-[17px] h-[17px] shadow-sm"
                >
                  {wishlistCount}
                </motion.span>
              )}
            </NavIconButton>

            {/* Blog */}
            <NavIconButton onClick={() => navigate("/בלוג")} aria-label="בלוג">
              <BookOpen size={19} className="text-primary" />
            </NavIconButton>

            {/* Search icon — neon glow */}
            <NavIconButton
              onClick={() => setDesktopSearchOpen(v => !v)}
              title="חיפוש"
              className="relative"
              style={{ filter: "drop-shadow(0 0 12px hsl(var(--primary) / 0.6))" }}
            >
              <Search size={19} className="text-primary" />
              <div className="absolute inset-0 rounded-xl bg-primary/10 blur-[4px] pointer-events-none" />
            </NavIconButton>

            {/* Theme toggle */}
            <NavIconButton onClick={toggleTheme} title={isDark ? "מצב יום" : "מצב לילה"}>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={theme}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.25 }}
                >
                  {isDark ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} className="text-primary" />}
                </motion.div>
              </AnimatePresence>
            </NavIconButton>

            {/* Cart */}
            <NavIconButton className="relative" onClick={() => openCart(true)} aria-label="עגלת קניות">
              <ShoppingCart size={19} />
              {itemCount > 0 && (
                <motion.span
                  key={itemCount}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                  className="absolute -top-0.5 -left-0.5 gradient-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center min-w-[17px] h-[17px] shadow-sm"
                >
                  {itemCount}
                </motion.span>
              )}
            </NavIconButton>

            {/* Separator */}
            <div className="w-px h-6 bg-primary/20 mx-1" />

            {user ? (
              <div className="flex items-center gap-1">
                <PointsBadge />
                <Suspense fallback={null}><DailyReward /></Suspense>
                {/* ═══ Profile trigger (desktop) ═══ */}
                <div className="relative">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setProfileMenuOpen((v) => !v)}
                    className="flex items-center gap-2.5 py-1.5 px-3 rounded-xl hover:bg-muted/60 transition-all duration-200 profile-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full gradient-primary text-primary-foreground flex items-center justify-center text-xs font-black ring-2 ring-primary/15 ring-offset-1 ring-offset-card shadow-sm">
                      {(user.email || "?")[0].toUpperCase()}
                    </div>
                    <div className="hidden lg:block text-right">
                      <p className="text-[11px] text-muted-foreground leading-none">שלום 👋</p>
                      <p className="text-[12px] font-bold text-foreground leading-tight mt-0.5 max-w-[100px] truncate">
                        {user.email?.split("@")[0] || "משתמש"}
                      </p>
                    </div>
                    <ChevronDown size={12} className="text-muted-foreground hidden lg:block" />
                  </motion.button>
                </div>
              </div>
            ) : (
              <Link to="/כניסה" aria-label="כניסה לחשבון">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center gap-2 py-2 px-4 rounded-xl bg-primary text-primary-foreground font-bold text-[13px] transition-all hover:scale-[1.03] shadow-[0_0_15px_hsl(var(--primary)/0.3)]"
                >
                  <User size={16} />
                  <span>כניסה</span>
                </motion.button>
              </Link>
            )}
          </div>

          {/* ═══ Shared Profile Dropdown (mobile + desktop) ═══ */}
          {user && (
            <div ref={profileMenuRef}>
              <AnimatePresence>
                {profileMenuOpen && (
                  <>
                    {/* Backdrop for mobile */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/20 z-[60] md:hidden"
                      onClick={() => setProfileMenuOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.97 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="fixed left-4 right-4 top-28 md:left-auto md:right-4 md:top-[72px] md:w-[300px] bg-popover rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15),0_0_0_1px_hsl(var(--border)/0.5)] overflow-hidden z-[70] max-h-[80vh] overflow-y-auto"
                    >
                      {/* User header with gradient */}
                      <div className="relative p-5 overflow-hidden">
                        <div className="absolute inset-0 gradient-primary opacity-[0.06]" />
                        <div className="absolute top-0 left-0 w-32 h-32 rounded-full bg-primary/10 blur-[60px] -translate-x-1/2 -translate-y-1/2" />
                        <div className="relative flex items-center gap-3.5">
                          <div className="w-12 h-12 rounded-2xl gradient-primary text-primary-foreground flex items-center justify-center text-lg font-black shrink-0 shadow-lg shadow-primary/25">
                            {(user.email || "?")[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-bold text-popover-foreground truncate text-sm leading-tight">
                              {user.email?.split("@")[0] || "משתמש"}
                            </p>
                            <p className="text-[11px] text-muted-foreground truncate mt-0.5">{user.email}</p>
                          </div>
                        </div>
                      </div>

                      {/* Quick stats row */}
                      <div className="grid grid-cols-3 gap-px bg-border/30 mx-4 rounded-xl overflow-hidden mb-3">
                        {[
                          { icon: Package, label: "הזמנות", path: "/הזמנות", value: null },
                          { icon: Ticket, label: "קופונים", path: "/קופונים", value: null },
                          { icon: Coins, label: "נקודות", path: "/פרופיל", value: pointsBalance.toLocaleString() },
                        ].map((item) => (
                          <button
                            key={item.label}
                            onClick={() => { navigate(item.path); setProfileMenuOpen(false); }}
                            className="flex flex-col items-center gap-1 py-3 bg-popover hover:bg-muted/60 transition-all duration-200 group first:rounded-r-xl last:rounded-l-xl"
                          >
                            <div className="w-9 h-9 rounded-xl bg-muted/70 group-hover:bg-primary/10 flex items-center justify-center transition-all duration-200 group-hover:scale-105">
                              <item.icon size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            {item.value && (
                              <span className="text-[11px] font-black text-primary">{item.value}</span>
                            )}
                            <span className="text-[10px] font-medium text-muted-foreground group-hover:text-popover-foreground transition-colors leading-none">
                              {item.label}
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* Menu links — include Profile link */}
                      <div className="px-2 pb-1">
                        {[
                          { icon: User, label: "הפרופיל שלי", path: "/פרופיל" },
                          { icon: Star, label: "הביקורות שלי", path: "/הביקורות-שלי" },
                          { icon: Heart, label: "רשימת בקשות", path: "/מועדפים" },
                          ...(isVendor ? [{ icon: Store, label: "פורטל ספקים", path: "/פורטל-ספקים" }] : []),
                          { icon: Headphones, label: "שירות לקוחות", path: "/מרכז-עזרה" },
                          { icon: Settings, label: "הגדרות", path: "/הגדרות" },
                        ].map((item) => (
                          <button
                            key={item.label}
                            onClick={() => { navigate(item.path); setProfileMenuOpen(false); }}
                            className="w-full flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-muted/60 hover:shadow-[0_0_12px_hsl(var(--primary)/0.08)] transition-all duration-200 text-right group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-muted/50 group-hover:bg-primary/10 flex items-center justify-center transition-all duration-200 shrink-0">
                              <item.icon size={15} className="text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <span className="text-[13px] font-medium text-popover-foreground flex-1">{item.label}</span>
                            <ChevronLeft size={13} className="text-muted-foreground/40 group-hover:text-muted-foreground group-hover:-translate-x-0.5 transition-all" />
                          </button>
                        ))}
                      </div>

                      {isAdmin && (
                        <div className="mx-2 mb-1">
                          <button
                            onClick={() => { navigate("/ניהול"); setProfileMenuOpen(false); }}
                            className="w-full flex items-center gap-3 py-2 px-3 rounded-xl bg-destructive/5 hover:bg-destructive/10 transition-all duration-200 text-right group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-destructive/10 group-hover:bg-destructive/15 flex items-center justify-center transition-all shrink-0">
                              <ShieldCheck size={15} className="text-destructive" />
                            </div>
                            <span className="text-[13px] font-bold text-destructive flex-1">פאנל ניהול</span>
                            <ChevronLeft size={13} className="text-destructive/40" />
                          </button>
                        </div>
                      )}

                      <div className="border-t border-border/40 mx-2 my-1" />

                      <div className="px-2 pb-2">
                        <button
                          onClick={async () => { await signOut(); setProfileMenuOpen(false); navigate("/"); }}
                          className="w-full flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-destructive/5 transition-all duration-200 group"
                        >
                          <div className="w-8 h-8 rounded-lg group-hover:bg-destructive/10 flex items-center justify-center transition-all shrink-0">
                            <LogOut size={15} className="text-muted-foreground group-hover:text-destructive transition-colors" />
                          </div>
                          <span className="text-[13px] font-medium text-muted-foreground group-hover:text-destructive transition-colors">התנתק</span>
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* ═══ Desktop Search Dropdown ═══ */}
        <AnimatePresence>
          {desktopSearchOpen && (
            <motion.div
              ref={searchRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="hidden md:block overflow-hidden border-t border-primary/20 bg-card/95 backdrop-blur-md shadow-[0_8px_30px_hsl(var(--primary)/0.1)]"
            >
              <div className="container py-3">
                <form className="relative max-w-2xl mx-auto" onSubmit={handleSearchSubmit}>
                  <input
                    type="text"
                    name="query"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setShowSearchPreview(true); }}
                    placeholder="חפש מוצרים, קטגוריות, מותגים..."
                    className="w-full rounded-xl border border-primary/30 bg-muted/20 py-3 pr-5 pl-24 text-sm outline-none transition-all duration-300 placeholder:text-muted-foreground/40 focus:border-primary/50 focus:bg-card focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.08),0_0_25px_hsl(var(--primary)/0.1)]"
                    autoFocus
                    onFocus={() => { setSearchFocused(true); if (searchQuery.trim().length >= 2) setShowSearchPreview(true); }}
                    onBlur={() => setSearchFocused(false)}
                  />
                  <div className="absolute left-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setVisualSearchOpen(true)}
                      className="rounded-lg p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                      title="חיפוש לפי תמונה"
                    >
                      <Camera size={16} />
                    </motion.button>
                    <motion.button
                      type="submit"
                      aria-label="חיפוש"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="gradient-primary rounded-lg p-2 text-primary-foreground shadow-sm shadow-primary/20"
                    >
                      <Search size={16} />
                    </motion.button>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setDesktopSearchOpen(false); setSearchQuery(""); setShowSearchPreview(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                  >
                    <X size={16} />
                  </button>
                </form>

                {/* Live preview */}
                {showSearchPreview && searchPreviewResults && searchPreviewResults.length > 0 && searchQuery.trim().length >= 2 && (
                  <div className="max-w-2xl mx-auto mt-2 bg-card border border-border/40 rounded-xl shadow-xl overflow-hidden">
                    {searchPreviewResults.map((product) => {
                      const discount = product.original_price > product.price
                        ? Math.round((1 - product.price / product.original_price) * 100)
                        : 0;
                      return (
                        <Link
                          key={product.id}
                          to={productUrl(product.name)}
                          onClick={() => { setShowSearchPreview(false); setSearchQuery(""); setDesktopSearchOpen(false); }}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 transition-colors border-b border-border/10 last:border-0"
                        >
                          <img src={product.image_url || "/placeholder.svg"} alt="" className="w-10 h-10 rounded-lg object-contain bg-muted/10 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium line-clamp-1">{product.name}</p>
                            <p className="text-[10px] text-muted-foreground">{product.category}</p>
                          </div>
                          <div className="text-left shrink-0">
                            <span className="text-xs font-bold text-[hsl(var(--price))]">₪{product.price}</span>
                            {discount > 0 && <span className="text-[9px] text-muted-foreground line-through mr-1">₪{product.original_price}</span>}
                          </div>
                        </Link>
                      );
                    })}
                    <button
                      onClick={() => { handleSearchSubmit(); setDesktopSearchOpen(false); }}
                      className="w-full px-3 py-2.5 text-xs font-medium text-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-1"
                    >
                      <Search size={12} />
                      הצג את כל התוצאות עבור "{searchQuery}"
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══ Mobile Search Overlay ═══ */}
        <AnimatePresence>
          {mobileSearchOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30 z-[80] md:hidden"
                onClick={() => setMobileSearchOpen(false)}
              />
              <motion.div
                ref={mobileSearchRef}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="fixed top-0 left-0 right-0 z-[90] bg-card shadow-xl md:hidden"
              >
                <div className="flex items-center gap-2 p-3">
                   <form
                     className="relative flex-1"
                     onSubmit={handleSearchSubmit}
                   >
                    <input
                      type="text"
                      name="query"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setShowSearchPreview(true); }}
                      placeholder="חפש מוצרים, קטגוריות, מותגים..."
                      className="w-full rounded-xl border border-primary/40 bg-muted/30 py-3 pr-4 pl-12 text-sm outline-none focus:border-primary focus:bg-card focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.1)] transition-all placeholder:text-muted-foreground/40"
                      autoFocus
                    />
                    <div className="absolute left-1.5 top-1/2 -translate-y-1/2">
                      <motion.button
                        type="submit"
                        aria-label="חיפוש"
                        whileTap={{ scale: 0.9 }}
                        className="gradient-primary rounded-lg p-2 text-primary-foreground shadow-sm"
                      >
                        <Search size={16} />
                      </motion.button>
                    </div>
                   </form>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => { setMobileSearchOpen(false); setSearchQuery(""); }}
                    className="p-2 rounded-xl hover:bg-muted/60 transition-colors shrink-0"
                  >
                    <X size={20} className="text-muted-foreground" />
                  </motion.button>
                </div>
                {/* Mobile live preview */}
                {showSearchPreview && searchPreviewResults && searchPreviewResults.length > 0 && searchQuery.trim().length >= 2 && (
                  <div className="border-t border-border/20 max-h-[60vh] overflow-y-auto">
                    {searchPreviewResults.map((product) => (
                      <Link
                        key={product.id}
                        to={productUrl(product.name)}
                        onClick={() => { setShowSearchPreview(false); setMobileSearchOpen(false); setSearchQuery(""); }}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors border-b border-border/10 last:border-0"
                      >
                        <img
                          src={product.image_url || "/placeholder.svg"}
                          alt=""
                          className="w-10 h-10 rounded-lg object-contain bg-muted/10 shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium line-clamp-1">{product.name}</p>
                          <p className="text-[10px] text-muted-foreground">{product.category}</p>
                        </div>
                        <span className="text-xs font-bold text-[hsl(var(--price))] shrink-0">₪{product.price}</span>
                      </Link>
                    ))}
                    <button
                      onClick={() => handleSearchSubmit()}
                      className="w-full px-3 py-2.5 text-xs font-medium text-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-1"
                    >
                      <Search size={12} />
                      הצג את כל התוצאות
                    </button>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>

      </nav>

      {/* ═══ Mobile menu drawer ═══ */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="lg:hidden fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-background z-50 overflow-y-auto shadow-[0_0_60px_rgba(0,0,0,0.2)]"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border/40">
                <img src={smileylandLogo} alt="SmileyGift" width={120} height={48} className="h-12 w-auto" />
                <motion.button
                  whileTap={{ scale: 0.85 }}
                  onClick={() => setMobileOpen(false)}
                  className="w-9 h-9 rounded-xl bg-muted/60 hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <X size={18} />
                </motion.button>
              </div>

              {/* User section */}
              {user ? (
                <div className="p-4 border-b border-border/40">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl gradient-primary text-primary-foreground flex items-center justify-center text-lg font-black shrink-0 shadow-lg shadow-primary/20">
                      {(user.email || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-foreground truncate text-sm">
                        {user.email?.split("@")[0] || "משתמש"}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { icon: Package, label: "הזמנות", path: "/הזמנות" },
                      { icon: Ticket, label: "קופונים", path: "/קופונים" },
                      { icon: Coins, label: `${pointsBalance} נק'`, path: "/פרופיל" },
                    ].map((item) => (
                      <Link
                        key={item.label}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-muted/40 hover:bg-muted/70 transition-all duration-200 group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-background group-hover:bg-primary/10 flex items-center justify-center transition-all shadow-sm">
                          <item.icon size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <span className="text-[11px] font-semibold text-foreground">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-4 border-b border-border/40">
                  <Link
                    to="/כניסה"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl gradient-primary text-primary-foreground font-bold text-sm shadow-lg shadow-primary/20"
                  >
                    <User size={18} />
                    כניסה / הרשמה
                  </Link>
                </div>
              )}

              {/* Account links */}
              {user && (
                <div className="px-3 py-3 border-b border-border/40">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 mb-2">חשבון</p>
                  {[
                    { icon: User, label: "הפרופיל שלי", path: "/פרופיל" },
                    { icon: Heart, label: "רשימת בקשות", path: "/מועדפים" },
                    { icon: Star, label: "הביקורות שלי", path: "/הביקורות-שלי" },
                    ...(isVendor ? [{ icon: Store, label: "פורטל ספקים", path: "/פורטל-ספקים" }] : []),
                  ].map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: 15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-muted/60 transition-all duration-200 group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-muted/50 group-hover:bg-primary/10 flex items-center justify-center transition-all shrink-0">
                          <item.icon size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <span className="text-sm font-medium text-foreground flex-1">{item.label}</span>
                        <ChevronLeft size={14} className="text-muted-foreground/40 group-hover:text-muted-foreground transition-all" />
                      </Link>
                    </motion.div>
                  ))}
                  {isAdmin && (
                    <motion.div initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
                      <Link
                        to="/ניהול"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-destructive/5 hover:bg-destructive/10 transition-all duration-200 mt-1 group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                          <ShieldCheck size={16} className="text-destructive" />
                        </div>
                        <span className="text-sm font-bold text-destructive flex-1">פאנל ניהול</span>
                        <ChevronLeft size={14} className="text-destructive/40" />
                      </Link>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Categories — hierarchical tree */}
              <div className="px-3 py-3 border-b border-border/40">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 mb-2">קטגוריות</p>
                <div className="space-y-0.5">
                  {categories.map((cat, i) => {
                    const isExpanded = mobileExpandedCats.has(i);
                    return (
                      <motion.div
                        key={cat.name}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.02 }}
                      >
                        <div className="flex items-center">
                          <Link
                            to={`/קטגוריה/${cat.slug}`}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-2.5 py-2.5 px-3 rounded-xl hover:bg-muted/60 transition-all duration-200 group flex-1 min-w-0"
                          >
                            <img src={cat.circleImage} alt={cat.name} className="w-7 h-7 rounded-full object-cover ring-1 ring-border/30 group-hover:ring-primary/30 transition-all shrink-0" />
                            <span className="text-[13px] font-medium text-foreground truncate">{cat.name}</span>
                          </Link>
                          {cat.subcategories.length > 0 && (
                            <button
                              onClick={() => {
                                setMobileExpandedCats(prev => {
                                  const next = new Set(prev);
                                  if (next.has(i)) next.delete(i); else next.add(i);
                                  return next;
                                });
                              }}
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted/60 transition-all shrink-0"
                            >
                              <motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
                                <ChevronLeft size={14} className="text-muted-foreground" />
                              </motion.div>
                            </button>
                          )}
                        </div>
                        <AnimatePresence>
                          {isExpanded && cat.subcategories.length > 0 && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="pr-10 pb-1 space-y-0.5">
                                {cat.subcategories.map((sub) => (
                                  <Link
                                    key={sub.name}
                                    to={`/קטגוריה/${cat.slug}/${encodeURIComponent(sub.name)}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center gap-2 py-2 px-3 rounded-lg text-[12px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                                  >
                                    <SubcategoryCircle
                                      name={sub.name}
                                      emoji={sub.emoji}
                                      staticImage={sub.staticImage}
                                      dbImage={sub.dbImage}
                                      gradientColor={cat.color}
                                      size="sm"
                                      className="ring-1 ring-border/20"
                                    />
                                    {sub.name}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Extra links */}
              <div className="px-3 py-3 border-b border-border/40">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 mb-2">עוד</p>
                {[
                  { icon: Sparkles, label: "הפוך לספק", path: "/הפוך-לספק", accent: true },
                  { icon: Headphones, label: "שירות לקוחות", path: "/מרכז-עזרה" },
                  { icon: Settings, label: "הגדרות", path: "/הגדרות" },
                ].map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-muted/60 transition-all duration-200 group"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${item.accent ? "bg-primary/10" : "bg-muted/50 group-hover:bg-primary/10"}`}>
                      <item.icon size={16} className={item.accent ? "text-primary" : "text-muted-foreground group-hover:text-primary transition-colors"} />
                    </div>
                    <span className={`text-sm font-medium flex-1 ${item.accent ? "text-primary font-bold" : "text-foreground"}`}>{item.label}</span>
                    <ChevronLeft size={14} className="text-muted-foreground/40" />
                  </Link>
                ))}
              </div>

              {/* Sign out */}
              {user && (
                <div className="p-4">
                  <button
                    onClick={async () => { await signOut(); setMobileOpen(false); navigate("/"); }}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-border/50 text-muted-foreground hover:text-destructive hover:border-destructive/30 hover:bg-destructive/5 transition-all duration-200 font-medium text-sm"
                  >
                    <LogOut size={15} />
                    התנתק מהחשבון
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <Suspense fallback={null}>
        <VisualSearchModal open={visualSearchOpen} onClose={() => setVisualSearchOpen(false)} />
      </Suspense>
    </header>
  );
};

export default Navbar;
